import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, MessageBar, MessageBarBody, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useReadPosReport, useUploads } from '../services/conversionsApi';
import type { PosReportReadResult } from '../types';

const useStyles = makeStyles({
  box: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  row: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3 },
});

/**
 * A journal's attached reports (wave 4 §5). Each is named, with View; on a draft each can be removed (it leaves the journal
 * at the next save; the upload itself is untouched, and attaching it again puts it back). A POS report can be READ: the
 * server names its kind and, once the engine reads figures (P2.5), returns them, which `onFields` hands to the journal.
 * It never answers with a blank: a kind not read yet, or not in the registry, says "enter the lines by hand".
 */
export default function JournalAttachments({
  clientId, documentIds, frozen, onRemove, onFields,
}: {
  clientId: string;
  documentIds: readonly string[];
  frozen: boolean;
  onRemove: (documentId: string) => void;
  onFields: (read: PosReportReadResult) => string | null;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const uploads = useUploads(clientId, true);
  const [reading, setReading] = useState<string | null>(null);
  const read = useReadPosReport(clientId, reading, reading !== null);
  const [filledNote, setFilledNote] = useState<string | null>(null);

  if (documentIds.length === 0) return null;

  const said = (r: PosReportReadResult): string => {
    if (r.status === 'unknownKind') return t('journalPage.attachments.read.unknownKind');
    if (r.status === 'notReadYet') {
      const period = r.period ? ` ${t('journalPage.attachments.read.period', { start: r.period.printedStart, end: r.period.printedEnd })}` : '';
      return `${t('journalPage.attachments.read.notReadYet', { report: r.kindLabel ?? '' })}${period}`;
    }
    return filledNote ?? t('journalPage.attachments.read.read', { count: r.fields.length });
  };

  return (
    <div className={styles.box}>
      <Text weight="semibold">{t('journalPage.attachments.heading')}</Text>
      {documentIds.map((id) => {
        const doc = (uploads.data ?? []).find((u) => u.id === id);
        const name = doc?.fileName ?? t('journalPage.attachments.unknown');
        return (
          <div key={id} className={styles.box}>
            <div className={styles.row}>
              <Text>{name}</Text>
              {doc?.isDeleted && <Text className={styles.muted}>{t('journalPage.attachments.uploadRemoved')}</Text>}
              <Button
                as="a"
                size="small"
                appearance="subtle"
                href={`/uploads/view?${new URLSearchParams({ clientId, documentId: id }).toString()}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('uploads.actions.viewOf', { name })}
              >
                {t('uploads.actions.view')}
              </Button>
              {doc?.category === 'posReport' && (
                <Button
                  size="small"
                  aria-label={t('journalPage.attachments.readOf', { name })}
                  onClick={() => {
                    setFilledNote(null);
                    setReading(id);
                    if (reading === id) void read.refetch();
                  }}
                >
                  {t('journalPage.attachments.readButton')}
                </Button>
              )}
              {!frozen && (
                <Button size="small" appearance="subtle" aria-label={t('journalPage.attachments.removeOf', { name })} onClick={() => onRemove(id)}>
                  {t('journalPage.attachments.remove')}
                </Button>
              )}
            </div>
            {reading === id && read.isFetching && <Spinner size="tiny" label={t('journalPage.attachments.read.reading')} />}
            {reading === id && read.isError && (
              <MessageBar intent="error">
                <MessageBarBody>{read.error instanceof Error ? read.error.message : t('common.unknownError')}</MessageBarBody>
              </MessageBar>
            )}
            {reading === id && read.data && !read.isFetching && (
              <MessageBar intent={read.data.status === 'read' ? 'success' : 'info'}>
                <MessageBarBody>
                  {read.data.status === 'read' && !filledNote ? (
                    <Button size="small" onClick={() => setFilledNote(onFields(read.data))}>
                      {t('journalPage.attachments.read.fill', { count: read.data.fields.length })}
                    </Button>
                  ) : said(read.data)}
                </MessageBarBody>
              </MessageBar>
            )}
          </div>
        );
      })}
      {!frozen && <Text className={styles.muted}>{t('journalPage.attachments.removeNote')}</Text>}
    </div>
  );
}
